import {
  pgTable,
  serial,
  integer,
  text,
  timestamp,
  date,
  boolean,
  varchar,
  jsonb,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

/**
 * Eligibility Events Audit Log
 *
 * Tracks all eligibility-related events for compliance and auditing purposes.
 * Stores snapshots of eligibility calculations when key events occur.
 */
export const eligibilityEvents = pgTable(
  "eligibility_events",
  {
    id: serial("id").primaryKey(),
    /** Employee this event relates to */
    employeeId: integer("employee_id").notNull(),
    /** Client (employer) the employee belongs to */
    clientId: integer("client_id").notNull(),
    /** Plan this eligibility calculation is for (optional - may be null for general status changes) */
    planId: integer("plan_id"),
    /**
     * Type of eligibility event:
     * - 'calculated': Initial eligibility calculation on hire
     * - 'enrolled': Employee completed enrollment
     * - 'waived': Employee waived coverage
     * - 'terminated': Coverage terminated (status change, termination, etc.)
     * - 'status_change': Employment status changed (PT→FT, FT→PT)
     * - 'recalculated': Eligibility recalculated due to plan or setting changes
     * - 'qle': Qualifying Life Event triggered eligibility change
     */
    eventType: text("event_type").notNull(),
    /** Employee's hire date at time of event */
    hireDate: date("hire_date"),
    /** Calculated eligibility date (hire + waiting period) */
    eligibilityDate: date("eligibility_date"),
    /** Calculated effective date (after entry timing applied) */
    effectiveDate: date("effective_date"),
    /** Enrollment deadline */
    enrollmentDueDate: date("enrollment_due_date"),
    /** Employee's employment type at time of event */
    employmentType: varchar("employment_type", { length: 50 }),
    /** Previous employment type (for status_change events) */
    previousEmploymentType: varchar("previous_employment_type", { length: 50 }),
    /** Whether employee was eligible at time of event */
    isEligible: boolean("is_eligible"),
    /** Reason for ineligibility (if not eligible) */
    ineligibilityReason: text("ineligibility_reason"),
    /** Whether the configuration was ACA compliant */
    acaCompliant: boolean("aca_compliant"),
    /** Total days from hire to effective date */
    totalWaitingDays: integer("total_waiting_days"),
    /**
     * Full snapshot of eligibility calculation for audit purposes
     * Contains: waitingPeriod, waitingPeriodType, entryDate, minHoursPerWeek, etc.
     */
    snapshotData: jsonb("snapshot_data"),
    /** Additional notes or context for the event */
    notes: text("notes"),
    /** User who triggered this event (null for system-generated) */
    createdBy: integer("created_by"),
    /** When this event was recorded */
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    // Index for querying events by employee
    employeeIdx: index("eligibility_events_employee_idx").on(t.employeeId),
    // Index for querying events by client
    clientIdx: index("eligibility_events_client_idx").on(t.clientId),
    // Index for querying events by plan
    planIdx: index("eligibility_events_plan_idx").on(t.planId),
    // Index for querying events by type
    eventTypeIdx: index("eligibility_events_type_idx").on(t.eventType),
    // Composite index for common query pattern
    clientEmployeeIdx: index("eligibility_events_client_employee_idx").on(
      t.clientId,
      t.employeeId
    ),
  })
);

// Insert schema for eligibility events
export const insertEligibilityEventSchema = createInsertSchema(eligibilityEvents, {
  eventType: z.enum([
    "calculated",
    "enrolled",
    "waived",
    "terminated",
    "status_change",
    "recalculated",
    "qle",
    "dependent_added",
    "dependent_enrolled",
  ]),
  snapshotData: z.record(z.any()).optional(),
}).omit({
  id: true,
  createdAt: true,
});

// Type exports
export type EligibilityEvent = typeof eligibilityEvents.$inferSelect;
export type InsertEligibilityEvent = z.infer<typeof insertEligibilityEventSchema>;

/**
 * Eligibility Event Type enum for type safety
 */
export enum EligibilityEventType {
  CALCULATED = "calculated",
  ENROLLED = "enrolled",
  WAIVED = "waived",
  TERMINATED = "terminated",
  STATUS_CHANGE = "status_change",
  RECALCULATED = "recalculated",
  QLE = "qle",
  DEPENDENT_ADDED = "dependent_added",
  DEPENDENT_ENROLLED = "dependent_enrolled",
}

/**
 * Employment Status Changes Audit Log
 *
 * Dedicated table for tracking employment status changes (PT↔FT, etc.)
 * with full audit trail including reason, effective dates, and benefit impacts.
 */
export const employmentStatusChanges = pgTable(
  "employment_status_changes",
  {
    id: serial("id").primaryKey(),
    /** Employee whose status changed */
    employeeId: integer("employee_id").notNull(),
    /** Client (employer) the employee belongs to */
    clientId: integer("client_id").notNull(),
    /** Previous employment status */
    previousStatus: varchar("previous_status", { length: 50 }).notNull(),
    /** New employment status */
    newStatus: varchar("new_status", { length: 50 }).notNull(),
    /** Previous scheduled hours per week (if applicable) */
    previousHours: integer("previous_hours"),
    /** New scheduled hours per week (if applicable) */
    newHours: integer("new_hours"),
    /** Date the status change takes effect */
    effectiveDate: date("effective_date").notNull(),
    /** Required reason for the change */
    reason: text("reason").notNull(),
    /**
     * Impact on benefits eligibility:
     * - 'gaining': Employee becoming eligible for benefits
     * - 'losing': Employee losing eligibility for benefits
     * - 'none': No change to eligibility (e.g., PT to contractor)
     */
    eligibilityImpact: varchar("eligibility_impact", { length: 20 }).notNull(),
    /** Calculated benefit end date (for losing eligibility) */
    benefitEndDate: date("benefit_end_date"),
    /** New eligibility date (for gaining eligibility) */
    newEligibilityDate: date("new_eligibility_date"),
    /** New effective date for coverage (for gaining eligibility) */
    newCoverageEffectiveDate: date("new_coverage_effective_date"),
    /** Whether notification was sent to employee */
    notificationSent: boolean("notification_sent").default(false),
    /** When notification was sent */
    notificationSentAt: timestamp("notification_sent_at"),
    /** User who made the status change */
    changedBy: integer("changed_by").notNull(),
    /** When the change was recorded */
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    // Index for querying by employee
    employeeIdx: index("status_changes_employee_idx").on(t.employeeId),
    // Index for querying by client
    clientIdx: index("status_changes_client_idx").on(t.clientId),
    // Index for querying by effective date
    effectiveDateIdx: index("status_changes_effective_date_idx").on(t.effectiveDate),
    // Index for eligibility impact queries
    eligibilityImpactIdx: index("status_changes_eligibility_impact_idx").on(t.eligibilityImpact),
  })
);

// Insert schema for employment status changes
export const insertEmploymentStatusChangeSchema = createInsertSchema(employmentStatusChanges, {
  eligibilityImpact: z.enum(["gaining", "losing", "none"]),
}).omit({
  id: true,
  createdAt: true,
});

// Type exports for employment status changes
export type EmploymentStatusChange = typeof employmentStatusChanges.$inferSelect;
export type InsertEmploymentStatusChange = z.infer<typeof insertEmploymentStatusChangeSchema>;

/**
 * Eligibility Impact enum for type safety
 */
export enum EligibilityImpact {
  GAINING = "gaining",
  LOSING = "losing",
  NONE = "none",
}
